import { useEffect, useMemo, useRef, useState } from "react";
import {
  AssistantRuntimeProvider,
  ComposerPrimitive,
  MessagePrimitive,
  ThreadPrimitive,
  useAuiState,
} from "@assistant-ui/react";
import { useAISDKRuntime } from "@assistant-ui/react-ai-sdk";
import {
  getToolOutput,
  useAgentChat,
} from "@cloudflare/ai-chat/react";
import { useAgent } from "agents/react";
import { isToolUIPart, type UIMessage } from "ai";
import { API_URL, apiFetch } from "../lib/api-client";
import type { AgenticOnboardingAgentState } from "../lib/onboarding";
import type { ApprovalReview } from "../lib/onboarding";

type Props = {
  authUserId: string;
  onStateChange: (state: AgenticOnboardingAgentState | undefined) => void;
  onComplete?: () => void;
};

function HiddenTool() {
  return null;
}

function approvalRequiredOutput(output: unknown) {
  return Boolean(
    output &&
    typeof output === "object" &&
    (output as { requiresApproval?: unknown }).requiresApproval === true,
  );
}

function approvalPreparationPartIndex(message: UIMessage) {
  return message.parts.findIndex((part) => {
    if (!isToolUIPart(part) || part.type !== "tool-saveSetupValues") {
      return false;
    }
    return approvalRequiredOutput(getToolOutput(part));
  });
}

function messageHasVisibleText(message: UIMessage) {
  const approvalPartIndex = approvalPreparationPartIndex(message);
  return message.parts.some((part, index) =>
    part.type === "text" &&
    part.text.trim().length > 0 &&
    (approvalPartIndex === -1 || index > approvalPartIndex));
}

function Message() {
  const message = useAuiState((state) => state.message);
  if (
    message.metadata
    && typeof message.metadata === "object"
    && (message.metadata as { internal?: unknown }).internal === true
  ) {
    return null;
  }
  const role = message.role;
  const approvalPartIndex = message.content.findIndex((part) =>
    part.type === "tool-call" &&
    part.toolName === "saveSetupValues" &&
    approvalRequiredOutput(part.result));
  const continuationText = approvalPartIndex === -1
    ? []
    : message.content.slice(approvalPartIndex + 1).flatMap((part) =>
      part.type === "text" ? [part.text] : []);
  const hasVisibleText = message.content.some((part) =>
    part.type === "text" && part.text.trim().length > 0);

  if (role === "assistant") {
    // Tool-only steps are an implementation detail of the same user turn.
    // Their content is intentionally hidden, so do not leave an empty bubble
    // on screen while the automatic tool continuation produces the reply.
    if (!hasVisibleText || (approvalPartIndex !== -1 && continuationText.length === 0)) {
      return null;
    }
  }

  return (
    <MessagePrimitive.Root
      className={`flex ${role === "user" ? "justify-end" : "justify-start"}`}
    >
      <div
        className={
          role === "user"
            ? "max-w-[85%] rounded-2xl rounded-br-md bg-[#173f5f] px-4 py-3 text-sm leading-6 text-white"
            : "max-w-[90%] rounded-2xl rounded-bl-md bg-slate-100 px-4 py-3 text-sm leading-6 text-slate-800"
        }
      >
        {approvalPartIndex === -1 ? (
          <MessagePrimitive.Content
            components={{ tools: { Fallback: HiddenTool } }}
          />
        ) : continuationText.map((text, index) => (
          <p key={index} className="whitespace-pre-wrap">{text}</p>
        ))}
      </div>
    </MessagePrimitive.Root>
  );
}

type DiagnosticEvent = {
  id: string;
  occurredAt: string;
  level: "debug" | "info" | "warn" | "error";
  category: string;
  name: string;
  requestId: string | null;
  traceId: string | null;
  conversationId: string | null;
  durationMs: number | null;
  metadata: Record<string, unknown>;
};

type DiagnosticMessage = {
  id: string;
  occurredAt: string;
  role: "user" | "assistant";
  content: string;
  eventId: string | null;
  requestId: string | null;
  traceId: string | null;
  conversationId: string;
};

function eventSummary(event: DiagnosticEvent) {
  const detail = Object.entries(event.metadata)
    .map(([key, value]) => `${key}=${String(value)}`)
    .join(" · ");
  return [
    new Date(event.occurredAt).toLocaleTimeString(),
	  event.traceId ? `turn ${event.traceId.slice(0, 8)}` : "",
    event.name,
    event.durationMs === null ? "" : `${event.durationMs}ms`,
    detail,
  ].filter(Boolean).join(" · ");
}

export function EventLoggerPanel({
  conversationId,
  refreshKey,
}: {
  conversationId: string;
  refreshKey: string;
}) {
  const [events, setEvents] = useState<DiagnosticEvent[]>([]);
  const [messages, setMessages] = useState<DiagnosticMessage[]>([]);
  const [status, setStatus] = useState<"idle" | "copied" | "failed">("idle");

  useEffect(() => {
    let cancelled = false;
    async function loadEvents() {
      try {
        const [eventResponse, messageResponse] = await Promise.all([
          apiFetch(
            `/api/v1/events?limit=30&conversationId=${encodeURIComponent(conversationId)}`,
            {},
            { log: false },
          ),
          apiFetch(
            `/api/v1/events/messages?limit=30&conversationId=${encodeURIComponent(conversationId)}`,
            {},
            { log: false },
          ),
        ]);
        if (cancelled) return;
        if (eventResponse.ok) {
          const body = await eventResponse.json() as { data: { items: DiagnosticEvent[] } };
          setEvents(body.data.items);
        }
        if (messageResponse.ok) {
          const body = await messageResponse.json() as { data: { items: DiagnosticMessage[] } };
          setMessages(body.data.items);
        }
      } catch {
        // Diagnostics must not add noise or interrupt the customer workflow.
      }
    }
    void loadEvents();
    return () => { cancelled = true; };
  }, [conversationId, refreshKey]);

  async function copyEvents() {
    try {
      await navigator.clipboard.writeText(JSON.stringify({ events, messages }, null, 2));
      setStatus("copied");
    } catch {
      setStatus("failed");
    }
  }

  return (
    <section aria-label="Event logger" className="border-t border-slate-200 bg-slate-950 px-3 py-3 text-slate-100">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-cyan-300">Event logger</p>
          <p className="mt-0.5 text-xs text-slate-400">Events and prototype transcript, linked by trace ID</p>
        </div>
        <button
          type="button"
          onClick={() => void copyEvents()}
          disabled={events.length === 0 && messages.length === 0}
          className="rounded-md border border-slate-600 px-2.5 py-1.5 text-xs font-semibold text-slate-100 hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {status === "copied" ? "Copied" : status === "failed" ? "Copy failed" : "Copy"}
        </button>
      </div>
      <div className="mt-2 max-h-32 overflow-y-auto rounded-md bg-slate-900 px-2.5 py-2 font-mono text-[11px] leading-5">
        {events.length ? events.map((event) => (
          <p key={event.id} className={event.level === "error" || event.level === "warn" ? "text-amber-300" : "text-slate-300"}>
            {eventSummary(event)}
          </p>
        )) : <p className="text-slate-500">No events recorded yet.</p>}
      </div>
      <div className="mt-3">
        <p className="text-xs font-semibold uppercase tracking-[0.14em] text-cyan-300">Conversation transcript</p>
        <div className="mt-2 max-h-48 overflow-y-auto rounded-md bg-slate-900 px-2.5 py-2 text-xs leading-5">
          {messages.length ? [...messages].reverse().map((message) => (
            <div key={message.id} className="mb-2 last:mb-0">
              <p className="font-mono text-[11px] text-slate-500">
                {new Date(message.occurredAt).toLocaleTimeString()} · {message.role} · {message.traceId ? `turn ${message.traceId.slice(0, 8)}` : "no trace"}
              </p>
              <p className="whitespace-pre-wrap text-slate-200">{message.content}</p>
            </div>
          )) : <p className="text-slate-500">No messages recorded yet.</p>}
        </div>
      </div>
    </section>
  );
}

export function ApprovalPanel({
  review,
  submitting,
  error,
  onApprove,
  onDismiss,
}: {
  review: ApprovalReview;
  submitting: boolean;
  error?: string;
  onApprove: () => void;
  onDismiss: () => void;
}) {
  return (
    <div className="mx-4 mb-3 rounded-xl border border-[#2f7e8b]/30 bg-[#eef8f8] p-4 text-sm text-slate-700">
      <p className="text-xs font-medium uppercase tracking-wide text-[#23636c]">
        Review before saving
      </p>
      <p className="mt-1 font-semibold text-slate-900">{review.title}</p>
      <p className="mt-1 whitespace-pre-line text-slate-600">
        {review.summary}
      </p>
      {error && (
        <p className="mt-3 rounded-md bg-red-50 px-3 py-2 text-xs text-red-700">
          {error}
        </p>
      )}
      <div className="mt-3 flex gap-2">
        <button
          type="button"
          onClick={onApprove}
          disabled={submitting}
          className="rounded-lg bg-[#173f5f] px-3 py-2 text-xs font-semibold text-white disabled:opacity-60"
        >
          {submitting ? "Saving…" : review.confirmationLabel}
        </button>
        <button
          type="button"
          onClick={onDismiss}
          disabled={submitting}
          className="rounded-lg border border-slate-300 px-3 py-2 text-xs font-semibold text-slate-700 disabled:opacity-60"
        >
          Not yet
        </button>
      </div>
    </div>
  );
}

export function TurnRecoveryPanel({
  issue,
  onRetry,
}: {
  issue: NonNullable<AgenticOnboardingAgentState["lastTurnIssue"]>;
  onRetry: () => void;
}) {
  const toolFailure = issue.kind === "tool_failure";
  return (
    <div className="mx-4 mb-3 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
      <p className="font-semibold">
        {toolFailure ? "I couldn’t save one detail." : "I couldn’t finish my reply."}
      </p>
      <p className="mt-1 text-xs leading-5 text-amber-800">
        {toolFailure
          ? "No change was made for that detail. Please check it and tell me again in a different way."
          : "Your saved setup details are still safe. Try again and I’ll continue from the current setup."}
      </p>
      {!toolFailure && (
        <button
          type="button"
          onClick={onRetry}
          className="mt-2 rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-xs font-semibold text-amber-900 hover:bg-amber-100"
        >
          Try again
        </button>
      )}
    </div>
  );
}

function Conversation({
	 authUserId,
  chat,
  state,
  onApproveSetup,
}: {
	 authUserId: string;
  chat: ReturnType<typeof useAgentChat>;
  state: AgenticOnboardingAgentState | undefined;
  onApproveSetup: (reviewFingerprint: string) => Promise<void>;
}) {
  const [submitting, setSubmitting] = useState(false);
  const [editingApproval, setEditingApproval] = useState(false);
  const [approvalError, setApprovalError] = useState<string>();
  const composerInputRef = useRef<HTMLTextAreaElement>(null);
  const activeSetup = state?.progress?.activeSetup;
  const review = activeSetup?.status === "awaiting_approval"
    ? activeSetup.approvalReview
    : null;
  const approval = review;
  const busy =
    chat.isStreaming || chat.isRecovering || chat.isToolContinuation;
  const chatReady =
    (chat.status as string) === "ready" || (chat.status as string) === "idle";
  const composerDisabled =
    !chatReady || busy || Boolean(approval && !editingApproval);
  const hasVisibleMessages = useMemo(
    () => chat.messages.some(messageHasVisibleText),
    [chat.messages],
  );

  useEffect(() => {
    if (!approval) {
      setSubmitting(false);
      setEditingApproval(false);
    }
  }, [approval]);

  useEffect(() => {
    setEditingApproval(false);
  }, [approval?.reviewFingerprint]);

  useEffect(() => {
    if (composerDisabled) return;

    const animationFrame = requestAnimationFrame(() => {
      composerInputRef.current?.focus();
    });
    return () => cancelAnimationFrame(animationFrame);
  }, [chat.messages.length, composerDisabled]);

  async function approveReview() {
    if (!approval || submitting) return;
    setSubmitting(true);
    setApprovalError(undefined);
    try {
      await onApproveSetup(approval.reviewFingerprint);
    } catch {
      setApprovalError("We couldn’t save this setup. Please try again.");
      setSubmitting(false);
    }
  }

  function dismissApproval() {
    if (!approval || submitting) return;
    setEditingApproval(true);
    setApprovalError(undefined);
  }

  function retryTurn() {
    void chat.sendMessage({
      text: "Please continue from the current saved setup state and give me the next useful question. Do not repeat a completed action.",
    });
  }

  return (
    <ThreadPrimitive.Root className="flex w-full flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white">
      <ThreadPrimitive.Viewport
        className="flex h-[360px] shrink-0 flex-col gap-3 overflow-y-auto px-4 py-5"
        autoScroll
      >
        {hasVisibleMessages ? (
          <ThreadPrimitive.Messages>{() => <Message />}</ThreadPrimitive.Messages>
        ) : (
          <div
            aria-live="polite"
            className="flex flex-1 flex-col justify-center px-5 py-8"
          >
            <p className="text-base font-semibold text-slate-950">
              Let’s get your business ready to operate
            </p>
            <p className="mt-2 max-w-md text-sm leading-6 text-slate-600">
              Answer naturally. I’ll save only details you state clearly and ask
              before creating real records.
            </p>
            <button
              type="button"
              onClick={() =>
                void chat.sendMessage({
                  text: "Hello, help me set up my business.",
                })
              }
              className="mt-5 w-fit rounded-lg bg-[#173f5f] px-3 py-2 text-sm font-semibold text-white transition hover:bg-[#12344f]"
            >
              Start setup
            </button>
          </div>
        )}
      </ThreadPrimitive.Viewport>

      {busy && (
        <p className="px-4 pb-2 text-xs text-slate-500" role="status">
          {chat.isRecovering
            ? "Reconnecting to your saved setup…"
            : chat.isToolContinuation
              ? "Saving that and choosing the next goal…"
              : "WorkView Agent is replying…"}
        </p>
      )}

      {approval && (
        <ApprovalPanel
          review={approval}
          submitting={submitting}
          error={approvalError}
          onApprove={() => void approveReview()}
          onDismiss={dismissApproval}
        />
      )}

      {approval && editingApproval && (
        <p className="mx-4 mb-3 rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600">
          Tell me what you would like to change. I’ll generate a fresh review.
        </p>
      )}

      {state?.lastTurnIssue && !approval && !busy && (
        <TurnRecoveryPanel issue={state.lastTurnIssue} onRetry={retryTurn} />
      )}

      {chat.connectionError && (
        <p className="mx-4 mb-3 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700">
          The connection failed. Your setup values are still saved; reconnect or
          refresh to resume.
        </p>
      )}

      <div className="border-t border-slate-200 bg-white p-3">
        {approval && !editingApproval ? (
          <p className="rounded-lg bg-slate-50 px-3 py-2 text-center text-xs text-slate-500">
            Choose an option above to continue.
          </p>
        ) : state?.progress?.complete ? (
          <p className="rounded-lg bg-emerald-50 px-3 py-2 text-center text-sm font-medium text-emerald-800">
            Initial setup complete. Your first job is ready.
          </p>
        ) : (
          <ComposerPrimitive.Root className="flex items-end gap-2 rounded-xl border border-slate-300 bg-white p-2 focus-within:border-[#2f7e8b] focus-within:ring-2 focus-within:ring-[#2f7e8b]/20">
            <ComposerPrimitive.Input
              ref={composerInputRef}
              disabled={composerDisabled}
              placeholder="Write your answer here…"
              className="max-h-32 min-h-10 flex-1 resize-none border-0 bg-transparent px-2 py-2 text-sm text-slate-900 outline-none placeholder:text-slate-400"
            />
            <ComposerPrimitive.Send
              disabled={composerDisabled}
              className="rounded-lg bg-[#173f5f] px-3 py-2 text-sm font-semibold text-white disabled:opacity-50"
            >
              Send
            </ComposerPrimitive.Send>
          </ComposerPrimitive.Root>
        )}
      </div>

      <EventLoggerPanel
        conversationId={`onboarding-${authUserId}`}
        refreshKey={`${chat.messages.length}:${busy}:${chat.connectionError ? "error" : "ok"}`}
      />
    </ThreadPrimitive.Root>
  );
}

export default function AgenticOnboardingAssistant({
  authUserId,
  onStateChange,
  onComplete,
}: Props) {
  const agent = useAgent<AgenticOnboardingAgentState>({
    agent: "WorkViewAgent",
    name: `onboarding-${authUserId}`,
    host: API_URL,
  });
  const [isInitializing, setIsInitializing] = useState(true);
  const [initializationError, setInitializationError] = useState<string>();
  const completionNotified = useRef(false);
  const chat = useAgentChat({
    agent,
    credentials: "include",
    autoContinueAfterToolResult: true,
    resume: true,
  });
  const runtime = useAISDKRuntime(chat);

  useEffect(() => {
    let cancelled = false;
    void agent
      .call("initializeOnboarding")
      .then(() => {
        if (!cancelled) setIsInitializing(false);
      })
      .catch((error) => {
        if (cancelled) return;
        setInitializationError(
          error instanceof Error
            ? error.message
            : "We couldn’t start your onboarding conversation.",
        );
        setIsInitializing(false);
      });
    return () => {
      cancelled = true;
    };
  }, [agent]);

  useEffect(() => {
    onStateChange(agent.state);
    const complete = Boolean(agent.state?.progress?.complete);
    if (complete && !completionNotified.current) {
      completionNotified.current = true;
      onComplete?.();
    }
    if (!complete) completionNotified.current = false;
  }, [agent.state, onComplete, onStateChange]);

  return (
    <AssistantRuntimeProvider runtime={runtime}>
      {isInitializing ? (
        <div className="flex min-h-[560px] items-center justify-center rounded-2xl border border-slate-200 bg-white p-6 text-sm text-slate-500">
          Loading your saved setup…
        </div>
      ) : initializationError ? (
        <div className="flex min-h-[560px] items-center justify-center rounded-2xl border border-red-200 bg-red-50 p-6 text-sm text-red-700">
          {initializationError}
        </div>
      ) : (
        <Conversation
          authUserId={authUserId}
          chat={chat}
          state={agent.state}
          onApproveSetup={async (reviewFingerprint) => {
            await agent.call("approveActiveSetup", [reviewFingerprint]);
          }}
        />
      )}
    </AssistantRuntimeProvider>
  );
}
